import { useState } from "react";
import type { ElementDefaults } from "../model/elementDefaults.ts";
import { useT } from "../i18n/hooks.ts";
import { Dialog } from "./Dialog.tsx";
import { DialogActions } from "./DialogActions.tsx";
import { useIsDirty } from "./useIsDirty.ts";
import { NumberField, TextField } from "./fields.tsx";

type DefaultsTab = "idLabels" | "subcatchment" | "nodeLink";

export function DefaultsDialog({
  defaults,
  onSave,
  onClose,
}: {
  defaults: ElementDefaults;
  onSave: (defaults: ElementDefaults) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [tab, setTab] = useState<DefaultsTab>("idLabels");
  const [form, setForm] = useState<ElementDefaults>(defaults);
  const dirty = useIsDirty(form);

  function setPrefix(kind: keyof ElementDefaults["idPrefixes"], value: string) {
    setForm((current) => ({
      ...current,
      idPrefixes: { ...current.idPrefixes, [kind]: value },
    }));
  }

  function setSubcatch(field: keyof ElementDefaults["subcatchment"], value: number) {
    setForm((current) => ({
      ...current,
      subcatchment: { ...current.subcatchment, [field]: value },
    }));
  }

  function setNode(field: keyof ElementDefaults["node"], value: number) {
    setForm((current) => ({ ...current, node: { ...current.node, [field]: value } }));
  }

  function setConduit(field: keyof ElementDefaults["conduit"], value: number) {
    setForm((current) => ({ ...current, conduit: { ...current.conduit, [field]: value } }));
  }

  // Every tab's <section> stays mounted; this just picks the class that
  // makes the non-active ones invisible.
  function sectionClass(key: DefaultsTab): string {
    return tab === key ? "options-section" : "options-section options-section-inactive";
  }

  const tabs: { key: DefaultsTab; label: string }[] = [
    { key: "idLabels", label: t("defaultsDialog.idLabels") },
    { key: "subcatchment", label: t("defaultsDialog.subcatchment") },
    { key: "nodeLink", label: t("defaultsDialog.nodeLink") },
  ];

  return (
    <Dialog title={t("defaultsDialog.title")} onClose={onClose} wide isDirty={dirty}>
      <div className="project-data-tabs" role="tablist">
        {tabs.map((tb) => (
          <button
            key={tb.key}
            className={tab === tb.key ? "button tab-active" : "button"}
            role="tab"
            aria-selected={tab === tb.key}
            onClick={() => setTab(tb.key)}
          >
            {tb.label}
          </button>
        ))}
      </div>
      <div className="options-dialog-body">
        <section className={sectionClass("idLabels")}>
          <TextField
            label={t("defaultsDialog.raingage")}
            value={form.idPrefixes.raingage}
            onChange={(v) => setPrefix("raingage", v)}
          />
          <TextField
            label={t("defaultsDialog.subcatchmentPrefix")}
            value={form.idPrefixes.subcatchment}
            onChange={(v) => setPrefix("subcatchment", v)}
          />
          <TextField
            label={t("defaultsDialog.junction")}
            value={form.idPrefixes.junction}
            onChange={(v) => setPrefix("junction", v)}
          />
          <TextField
            label={t("defaultsDialog.outfall")}
            value={form.idPrefixes.outfall}
            onChange={(v) => setPrefix("outfall", v)}
          />
          <TextField
            label={t("defaultsDialog.divider")}
            value={form.idPrefixes.divider}
            onChange={(v) => setPrefix("divider", v)}
          />
          <TextField
            label={t("defaultsDialog.storage")}
            value={form.idPrefixes.storage}
            onChange={(v) => setPrefix("storage", v)}
          />
          <TextField
            label={t("defaultsDialog.conduit")}
            value={form.idPrefixes.conduit}
            onChange={(v) => setPrefix("conduit", v)}
          />
          <TextField
            label={t("defaultsDialog.pump")}
            value={form.idPrefixes.pump}
            onChange={(v) => setPrefix("pump", v)}
          />
          <TextField
            label={t("defaultsDialog.orifice")}
            value={form.idPrefixes.orifice}
            onChange={(v) => setPrefix("orifice", v)}
          />
          <TextField
            label={t("defaultsDialog.weir")}
            value={form.idPrefixes.weir}
            onChange={(v) => setPrefix("weir", v)}
          />
          <TextField
            label={t("defaultsDialog.outlet")}
            value={form.idPrefixes.outlet}
            onChange={(v) => setPrefix("outlet", v)}
          />
          <NumberField
            label={t("defaultsDialog.idIncrement")}
            value={form.idIncrement}
            onChange={(v) => setForm((current) => ({ ...current, idIncrement: Math.max(1, v) }))}
          />
        </section>

        <section className={sectionClass("subcatchment")}>
          <NumberField
            label={t("defaultsDialog.area")}
            value={form.subcatchment.area}
            onChange={(v) => setSubcatch("area", v)}
          />
          <NumberField
            label={t("defaultsDialog.width")}
            value={form.subcatchment.width}
            onChange={(v) => setSubcatch("width", v)}
          />
          <NumberField
            label={t("defaultsDialog.slopePercent")}
            value={form.subcatchment.slopePercent}
            onChange={(v) => setSubcatch("slopePercent", v)}
          />
          <NumberField
            label={t("defaultsDialog.imperviousPercent")}
            value={form.subcatchment.imperviousPercent}
            onChange={(v) => setSubcatch("imperviousPercent", v)}
          />
          <NumberField
            label={t("defaultsDialog.nImperv")}
            value={form.subcatchment.nImperv}
            onChange={(v) => setSubcatch("nImperv", v)}
          />
          <NumberField
            label={t("defaultsDialog.nPerv")}
            value={form.subcatchment.nPerv}
            onChange={(v) => setSubcatch("nPerv", v)}
          />
          <NumberField
            label={t("defaultsDialog.dStoreImperv")}
            value={form.subcatchment.dStoreImperv}
            onChange={(v) => setSubcatch("dStoreImperv", v)}
          />
          <NumberField
            label={t("defaultsDialog.dStorePerv")}
            value={form.subcatchment.dStorePerv}
            onChange={(v) => setSubcatch("dStorePerv", v)}
          />
          <NumberField
            label={t("defaultsDialog.pctZeroImperv")}
            value={form.subcatchment.pctZeroImperv}
            onChange={(v) => setSubcatch("pctZeroImperv", v)}
          />
        </section>

        <section className={sectionClass("nodeLink")}>
          <NumberField
            label={t("defaultsDialog.invertElevation")}
            value={form.node.invert}
            onChange={(v) => setNode("invert", v)}
          />
          <NumberField
            label={t("defaultsDialog.maxDepth")}
            value={form.node.maxDepth}
            onChange={(v) => setNode("maxDepth", v)}
          />
          <NumberField
            label={t("defaultsDialog.pondedArea")}
            value={form.node.pondedArea}
            onChange={(v) => setNode("pondedArea", v)}
          />
          <NumberField
            label={t("defaultsDialog.conduitLength")}
            value={form.conduit.length}
            onChange={(v) => setConduit("length", v)}
          />
          <NumberField
            label={t("defaultsDialog.conduitRoughness")}
            value={form.conduit.roughness}
            onChange={(v) => setConduit("roughness", v)}
          />
        </section>
      </div>
      <DialogActions
        primary={{ label: t("common.save"), onClick: () => onSave(form) }}
        onClose={onClose}
      />
    </Dialog>
  );
}
